import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';
import Reschedule, { RescheduleNotFound } from '../reschedule/reschedule';
import { postLoginDestination } from '../login/post-login-destination';
import { useSession } from '../../contexts/session-context';

/**
 * `/ux/reschedule/:orderId/:taskId/:guid?` (ROBO-616) — who is asking, decided once, here.
 *
 *   signed in            → the session. The guid in the address, if any, is NOT sent: the API uses
 *                          the session when it has one, so the guid would be a spare credential.
 *   no session + a guid  → a guest. Every call carries `hd-download-guid` and no bearer token.
 *   no session, no guid  → the uniform "No schedulable appointment was found." state, with no
 *                          request made. There is no sign-in prompt, as in legacy.
 *
 * The session check has to finish before the first read, or someone signed in would be read as a
 * guest (and attributed as the site owner). Hence the spinner while `authorize` runs.
 *
 * Legacy `Dashboard/reschedule.asp` has no login gate at all — a caller with neither a session nor
 * a guid gets "No schedulable product found." This page does the same (Herb, 2026-10-02).
 */

/** A guid as it may be sent in a header: hex and dashes only. Anything else is not a guid. */
function cleanGuid(value: string | undefined): string | undefined {
    const cleaned = (value ?? '').replace(/[^0-9a-fA-F-]/g, '');
    return cleaned.length > 0 ? cleaned : undefined;
}

/**
 * ROBO-695. `?returnUrl=` — where a signed-in user came from, set by legacy's redirect from
 * `Dashboard/reschedule.asp` (its `sRetUrl`, e.g. the site summary page a staff member opened this
 * from). ★ A LOCAL PATH ONLY: one leading slash, no second slash or backslash (`//host`, `/\\host`),
 * no scheme and no control characters, so the page can never be made to send someone to another site.
 * Anything else is ignored and the usual destination is used.
 */
export function safeReturnPath(value: string | null): string | undefined {
    if (!value || value.length > 2000) return undefined;
    if (!value.startsWith('/') || value.startsWith('//')) return undefined;
    for (const character of value) {
        if (character === '\\' || character.charCodeAt(0) < 0x20) return undefined;
    }
    return value;
}

function toId(value: string | undefined): number {
    return value && /^\d+$/.test(value) ? Number(value) : 0;
}

export default function HubReschedulePage() {
    const { authorize, status, isAuthenticated, user } = useSession();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const authorizeRan = useRef(false);
    // Latched: once the boot check has answered, a later `loading` (there is none today, but the
    // login form must never be swapped for a spinner mid-attempt — ROBO-483) does not hide the page.
    const [isSessionKnown, setIsSessionKnown] = useState(false);

    useEffect(() => {
        if (authorizeRan.current) return;
        authorizeRan.current = true;
        // Already resolved by an earlier hub page in this document: do not flip it back to loading.
        if (status !== 'idle') return;
        void authorize();
    }, [authorize, status]);

    useEffect(() => {
        if (status === 'authenticated' || status === 'unauthenticated') setIsSessionKnown(true);
    }, [status]);

    const orderId = toId(params.orderId);
    const taskId = toId(params.taskId);
    const guid = cleanGuid(params.guid);
    const returnPath = safeReturnPath(searchParams.get('returnUrl'));

    if (!isSessionKnown) {
        return (
            <Box
                role="status"
                aria-label="Loading"
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ height: '100vh', width: '100vw', overflow: 'hidden' }}
            >
                <CircularProgress color="inherit" size={32} sx={{ opacity: 0.25 }} />
            </Box>
        );
    }

    if (isAuthenticated) {
        return (
            <Reschedule
                orderId={orderId}
                taskId={taskId}
                exitPath={returnPath ?? postLoginDestination(user)}
            />
        );
    }

    if (guid) {
        return <Reschedule orderId={orderId} taskId={taskId} downloadGuid={guid} />;
    }

    return <RescheduleNotFound />;
}
